/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

const PALETTE_FILL_TYPE = {
	ColorPalette: 'simple',
	HeatPalette: 'continuous',
	ConditionalPalette: 'simple'
};

const PREFIX_CM_ID = '__CM__';

const PALETTE_SECTION_ORDER = [
	'mru',
	'local',
	'custom',
	'global',
	'system',
	'legacy'
];

/**
 * This class help UI Components render palettes group by sections
 */
export default class PaletteHelper {
	constructor({ paletteService, getPalettesFn, paletteName = null, maxMostRecentUseSize = 3 }) {
		if (!paletteService) {
			throw new Error(`Invalid paletteService`);
		}
		this.paletteService = paletteService;
		this.paletteName = paletteName || 'ColorPalette';
		this.getPalettesFn = getPalettesFn;
		this.maxMostRecentUseSize = maxMostRecentUseSize;
		this.palettes = null;
	}

	/**
	 * Retrieve palettes return from palette service, create appropriate sections for the palette to be render in the UI
	 *
	 * @returns {promise} resolved with a palette object key by sub sections of palettes
	 */
	getPaletteGroupBySections () {
		return this.getPalettesFn()
			.then(palettes => {
				// Clone the palette to make it local to this instance to avoid modifying its content by reference
				this.palettes = JSON.parse(JSON.stringify(palettes));
				this._creastePaletteSections();
				return this._getMostRecentlyUsed()
					.then(mru => {
						this.palettes.mru = mru || [];
						return this.palettes;
					});
			});
	}

	/**
	 * Return the order of sections to be render in the UI
	 *
	 * @returns {array} Array of section names
	 */
	getPaletteSectionOrder () {
		// Return a clone to avoid changing the original global definition
		return JSON.parse(JSON.stringify(PALETTE_SECTION_ORDER));
	}

	refreshPalette () {
		return this.getPaletteGroupBySections();
	}

	getPalette (paletteId) {
		const foundPalette = [];
		const sectionNameList = this.getPaletteSectionOrder();
		this._findMostRecentPaletteFromIds([paletteId], foundPalette, sectionNameList, 0);
		return foundPalette.length ? foundPalette[0] : null;
	}

	isSamePaletteId (idToVerify = '', idToVerifyAgainst ='') {
		return idToVerifyAgainst.includes(idToVerify);
	}

	static getCustomPaletteId (paletteId) {
		return paletteId.includes(PREFIX_CM_ID) ? paletteId : `${PREFIX_CM_ID}${paletteId}`;
	}

	static isCustomPalette (paletteId) {
		return paletteId.includes(PREFIX_CM_ID);
	}

	/**
	 * Return a list of most recently used palettes
	 *
	 * @returns {promise} resolved with a list of most recently used palettes
	 */
	_getMostRecentlyUsed () {
		const fillType = PALETTE_FILL_TYPE[this.paletteName] || 'simple';
		const mruPalettes = [];

		return this.paletteService.getRecentlyUsedPaletteInfo(fillType).then((mostRecentlyUsed) => {
			const mruIds = mostRecentlyUsed.map(mru => mru.id);
			const sectionNameList = this.getPaletteSectionOrder();
			const startingIndex = 1; // Skip over the mru since it is the one we want to create
			this._findMostRecentPaletteFromIds(mruIds, mruPalettes, sectionNameList, startingIndex);
			mruPalettes.length = (mruPalettes.length <= this.maxMostRecentUseSize) ? mruPalettes.length : this.maxMostRecentUseSize;
			return mruPalettes;
		});
	}

	/**
	 * Retrieve the most recent used palettes base on MRU palette ids
	 *
	 * @param {String[]} mruIds  Array of MRU ids to search for their corresponding palettes
	 * @param {Object[]} foundPaletteList  Array found palettes
	 * @param {String[]} sectionNameList - Array of palette section names
	 */
	_findMostRecentPaletteFromIds (mruIds, foundPaletteList, sectionNameList) {
		let outerIndex = 0;
		let innerIndex;
		for (outerIndex; outerIndex < mruIds.length; outerIndex++) {
			const idToSearch = mruIds[outerIndex];
			for (innerIndex = 0; innerIndex < sectionNameList.length; innerIndex++) {
				const sectionName = sectionNameList[innerIndex];
				const palette = this.palettes[sectionName];
				const matchedPalette = palette && palette.find(candidatePalette => {
					return ((candidatePalette.id == idToSearch) || (candidatePalette.id.indexOf(idToSearch) !== -1));
				});
				if (matchedPalette) {
					foundPaletteList.push(matchedPalette);
					break;
				}
			}
		}
	}

	/**
	 * Creates a palette sections as defined by PALETTE_SECTION_ORDER
	 */
	_creastePaletteSections () {
		// Make sure these array exists
		this.palettes.my = this.palettes.my || [];
		this.palettes.public = this.palettes.public || [];
		this.palettes.system = this.palettes.system || [];
		this.palettes.local = this.palettes.local || [];
		this.palettes.legacy = this.palettes.legacy || [];

		this.palettes.global = [...this.palettes.public];
		this.palettes.custom = [...this.palettes.my.filter(palette => {
			return !palette.public;
		})];
		delete this.palettes.my;
		delete this.palettes.public;
	}
}



// WEBPACK FOOTER //
// ./src/js/utilities/PaletteHelper.js